import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ContactUsService } from '../../services/contact-us.service';

@Component({
  selector: 'app-product-category-fields',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './product-category-fields.component.html'
})
export class ProductCategoryFieldsComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() lang: string;
  @Input() columnClass = 'col-md-4';

  insuranceTypes: any[] = [];
  products: any[] = [];
  loadingInsuranceTypes = true;
  loadingProducts = false;

  private destroy$ = new Subject<void>();

  constructor(private contactUsService: ContactUsService) {}

  get labels() { return this.contactUsService.commonLabels; }
  get placeholders() { return this.contactUsService.placeholderTextLabels; }
  get messages() { return this.contactUsService.messageLabels; }

  hasAvailableProducts(): boolean {
    return this.products.some(product => product.ItemID !== 'other');
  }

  ngOnInit() {
    this.form.get('productCategory')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const selected = this.insuranceTypes.find(t => t.DisplayName === value);
      const selectedId = selected?.ItemID || '';
      this.form.get('productCategoryId')?.setValue(selectedId);

      const otherInsurance = this.form.get('otherInsurance');
      const product = this.form.get('product');
      if (selectedId === 'other') {
        otherInsurance?.setValidators([Validators.required]);
        product?.clearValidators();
        product?.setValue('');
      } else {
        otherInsurance?.clearValidators();
        otherInsurance?.setValue('');
        product?.setValidators([Validators.required]);
      }
      otherInsurance?.updateValueAndValidity();
      product?.updateValueAndValidity();

      this.form.get('product')?.setValue('');
      this.products = [];
      if (selectedId === 'other') {
        this.products = [];
      } else if (selectedId) {
        this.loadingProducts = true;
        this.contactUsService.getProducts(selectedId, this.lang).subscribe(data => {
          const sorted = [...data].sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
          this.products = sorted.length > 0
            ? [...sorted, { ItemID: 'other', DisplayName: this.placeholders.other }]
            : [];
          if (!this.hasAvailableProducts()) {
            product?.clearValidators();
            this.form.get('productId')?.setValue('');
            const otherProduct = this.form.get('otherProduct');
            otherProduct?.clearValidators();
            otherProduct?.setValue('');
            otherProduct?.updateValueAndValidity();
          } else {
            product?.setValidators([Validators.required]);
          }
          product?.updateValueAndValidity();
          this.loadingProducts = false;
        });
      }
    });

    this.form.get('product')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const selected = this.products.find(p => p.DisplayName === value);
      const selectedId = selected?.ItemID || '';
      this.form.get('productId')?.setValue(selectedId);

      const otherProduct = this.form.get('otherProduct');
      if (selectedId === 'other') {
        otherProduct?.setValidators([Validators.required]);
      } else {
        otherProduct?.clearValidators();
        otherProduct?.setValue('');
      }
      otherProduct?.updateValueAndValidity();
    });

    this.contactUsService.getProductCategories(this.lang).subscribe(data => {
      const sorted = [...data.filter(t => t.DisplayName !== 'Supplemental Health')].sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
      this.insuranceTypes = [...sorted, { ItemID: 'other', DisplayName: this.placeholders.other }];
      this.loadingInsuranceTypes = false;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }
}
